import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate } from 'react-router-dom';
import './App.css';

interface ROMResult {
  game_name: string;
  system: string;
  site: string;
  url: string;
  size: string;
  description: string;
  cover_image: string;
}

interface GameDetails {
  game_name: string;
  site: string;
  url: string;
  size: string;
  year: string;
  region: string;
  players: string;
  serial: string;
  rating: string;
  genre: string;
  languages: string;
  description: string;
  cover_image: string;
}

const Home: React.FC = () => (
  <div className="home-page">
    <div className="home-hero">
      <div className="home-tagline">THE RETRO ROM SEARCH ENGINE</div>
      <p className="home-desc">
        Search for retro game ROMs across Vimm's Lair, ROMsGames,
        CDRomance, and LoLROMs — all from one place.
      </p>
      <Link to="/search" className="home-cta">▶ SEARCH ROMS</Link>
    </div>

    <div className="home-grid">
      <div className="home-card">
        <h3>28 CONSOLES</h3>
        <p>NES · SNES · N64 · GameCube · Wii · GB · GBC · GBA · DS · 3DS · PS1 · PS2 · PSP · Genesis · Dreamcast · Saturn · and more</p>
      </div>
      <div className="home-card">
        <h3>4 SOURCES</h3>
        <p>Vimm's Lair · ROMsGames · CDRomance · LoLROMs — results interleaved so no single site dominates</p>
      </div>
      <div className="home-card">
        <h3>FULL METADATA</h3>
        <p>Cover images · file size · region · year · genre · languages · serial number · ratings</p>
      </div>
    </div>

    <div className="home-mcp">
      <h2>MCP SERVER</h2>
      <p className="home-mcp-desc">
        ROMulus exposes a hosted MCP server. Any Claude instance, Cursor,
        or MCP-compatible agent can connect with one line — no install required.
      </p>
      <div className="home-tools">
        <div className="home-tool">
          <span className="tool-name">search_roms</span>
          <span className="tool-desc">Find ROMs by title and console</span>
        </div>
        <div className="home-tool">
          <span className="tool-name">get_game_details</span>
          <span className="tool-desc">Full metadata for any game page URL</span>
        </div>
        <div className="home-tool">
          <span className="tool-name">list_systems</span>
          <span className="tool-desc">All 28 supported consoles</span>
        </div>
      </div>
      <div className="home-snippet">
        <div className="snippet-label">CONNECT</div>
        <pre>{`{
  "mcpServers": {
    "romulus": {
      "type": "http",
      "url": "https://romulus.rocks/mcp/"
    }
  }
}`}</pre>
      </div>
      <div className="home-links">
        <a href="/api/openapi.json" target="_blank" rel="noopener noreferrer">↗ OPENAPI SPEC</a>
        <a href="https://github.com/31780/romulus" target="_blank" rel="noopener noreferrer">↗ SOURCE</a>
        <a href="https://smithery.ai/servers/openclaw-hal9000/romulus-31780" target="_blank" rel="noopener noreferrer">
          <img src="https://smithery.ai/badge/openclaw-hal9000/romulus-31780" alt="Smithery badge" style={{imageRendering: 'auto', height: '20px'}} />
        </a>
      </div>
    </div>

  </div>
);

const Legal: React.FC = () => (
  <div className="legal-page">
    <h2>LEGAL INFORMATION</h2>

    <section>
      <h3>DISCLAIMER</h3>
      <p>
        ROMulus is a search engine. We index publicly available information from
        third-party websites and do not host, store, cache, or distribute any ROM
        files, game images, or other copyrighted content. All search results link
        directly to external sites over which we have no control.
      </p>
    </section>

    <section>
      <h3>DMCA &amp; TAKEDOWN POLICY</h3>
      <p>
        If you are a copyright holder and believe a search result indexes content
        that infringes your rights, please send a written notice to{' '}
        <span className="legal-contact">help@romulus.rocks</span>. Include: (1) identification
        of the copyrighted work; (2) the specific URL appearing in our index;
        (3) your contact information; (4) a statement of good faith belief; and
        (5) a statement of accuracy under penalty of perjury. We will remove the
        relevant result promptly upon verification.
      </p>
    </section>

    <section>
      <h3>TERMS OF USE</h3>
      <p>
        By using ROMulus you agree that: (1) you are solely responsible for
        ensuring your use of any linked content complies with the laws of your
        jurisdiction; (2) ROMulus makes no representations about the legality of
        any content found through its index; (3) ROMulus is provided &ldquo;as
        is&rdquo; without warranty of any kind.
      </p>
      <p>
        Do not use this service to infringe copyright or circumvent access
        controls. Users who misuse the service may be blocked.
      </p>
    </section>

    <section>
      <h3>A NOTE ON ROM OWNERSHIP</h3>
      <p>
        Many people believe that owning a physical copy of a game grants the legal
        right to download a ROM backup. <strong>This is not established law</strong> in
        the United States or most other jurisdictions. No court has upheld this
        defense. You should consult a lawyer in your country before downloading
        any ROM file for a game still under copyright.
      </p>
      <p>
        ROMs for games that are genuinely in the public domain, or distributed
        under open licenses by their rights holders, are legal to download freely.
      </p>
    </section>
  </div>
);

const AppContent: React.FC = () => {
  const [query, setQuery] = useState('');
  const [system, setSystem] = useState('all');
  const [results, setResults] = useState<ROMResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [detailsCache, setDetailsCache] = useState<Record<string, GameDetails | 'loading' | 'error'>>({});
  const [visits, setVisits] = useState<number | null>(null);

  useEffect(() => {
    axios.get('/api/visits').then(res => setVisits(res.data.visits)).catch(() => {});
  }, []);

  const toggleDetails = async (index: number, url: string) => {
    if (expandedIndex === index) {
      setExpandedIndex(null);
      return;
    }
    setExpandedIndex(index);
    if (detailsCache[url]) return;
    setDetailsCache(prev => ({ ...prev, [url]: 'loading' }));
    try {
      const res = await axios.get('/api/details', { params: { url } });
      setDetailsCache(prev => ({ ...prev, [url]: res.data }));
    } catch {
      setDetailsCache(prev => ({ ...prev, [url]: 'error' }));
    }
  };
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const resetSearch = () => {
    setQuery('');
    setResults([]);
    setError('');
    setSystem('all');
    setSearched(false);
    setExpandedIndex(null);
    setDetailsCache({});
    navigate('/search');
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const searchROMs = async () => {
    if (!query.trim()) return;

    setLoading(true);
    setError('');

    try {
      const response = await axios.get('/api/search', {
        params: { q: query, system }
      });
      setResults(response.data.results);
    } catch (err) {
      setError('Failed to search ROMs');
      console.error(err);
    } finally {
      setLoading(false);
      setSearched(true);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      searchROMs();
    }
  };

  const SYSTEM_LABELS: Record<string, string> = {
    nes: 'NES', snes: 'SNES', n64: 'N64', gamecube: 'GameCube', wii: 'Wii',
    gb: 'Game Boy', gbc: 'Game Boy Color', gba: 'GBA', ds: 'DS', '3ds': '3DS', vb: 'Virtual Boy',
    sms: 'Master System', genesis: 'Genesis', segacd: 'Sega CD', '32x': '32X',
    gg: 'Game Gear', saturn: 'Saturn', dreamcast: 'Dreamcast',
    ps1: 'PS1', ps2: 'PS2', ps3: 'PS3', psp: 'PSP',
    atari2600: 'Atari 2600', atari5200: 'Atari 5200', atari7800: 'Atari 7800', lynx: 'Lynx',
    tg16: 'TurboGrafx-16', xbox: 'Xbox',
  };

  return (
      <div className="app">
        <header className="header">
          <h1>ROMulus</h1>
          <p>INSERT COIN ▶ PRESS START</p>
        </header>

        <nav className="nav">
          <Link to="/">Home</Link>
          <Link to="/search">Search</Link>
          <Link to="/legal">Legal</Link>
        </nav>

        <main className="main">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/search" element={
              <div className="search-container">
                <div className="search-box">
                  <input
                    ref={inputRef}
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="ENTER GAME TITLE..."
                    className="search-input"
                  />
                  <select
                    value={system}
                    onChange={(e) => setSystem(e.target.value)}
                    className="system-select"
                  >
                    <option value="all">All Platforms</option>
                    <optgroup label="Nintendo">
                      <option value="nes">NES</option>
                      <option value="snes">SNES</option>
                      <option value="n64">N64</option>
                      <option value="gamecube">GameCube</option>
                      <option value="wii">Wii</option>
                    </optgroup>
                    <optgroup label="Game Boy / DS">
                      <option value="gb">Game Boy</option>
                      <option value="gbc">Game Boy Color</option>
                      <option value="gba">GBA</option>
                      <option value="ds">DS</option>
                      <option value="3ds">3DS</option>
                      <option value="vb">Virtual Boy</option>
                    </optgroup>
                    <optgroup label="Sega">
                      <option value="sms">Master System</option>
                      <option value="genesis">Genesis</option>
                      <option value="segacd">Sega CD</option>
                      <option value="32x">32X</option>
                      <option value="gg">Game Gear</option>
                      <option value="saturn">Saturn</option>
                      <option value="dreamcast">Dreamcast</option>
                    </optgroup>
                    <optgroup label="PlayStation">
                      <option value="ps1">PS1</option>
                      <option value="ps2">PS2</option>
                      <option value="ps3">PS3</option>
                      <option value="psp">PSP</option>
                    </optgroup>
                    <optgroup label="Atari">
                      <option value="atari2600">Atari 2600</option>
                      <option value="atari5200">Atari 5200</option>
                      <option value="atari7800">Atari 7800</option>
                      <option value="lynx">Lynx</option>
                    </optgroup>
                    <optgroup label="Other">
                      <option value="tg16">TurboGrafx-16</option>
                      <option value="xbox">Xbox</option>
                    </optgroup>
                  </select>
                  <button
                    onClick={searchROMs}
                    disabled={loading}
                    className="search-button"
                  >
                    {loading ? '...' : '▶ GO'}
                  </button>
                </div>

                {loading && (
                  <div className="loading-bar-wrap">
                    <div className="loading-bar-label">SEARCHING<span className="loading-dots" /></div>
                    <div className="loading-bar">
                      <div className="loading-bar-fill" />
                    </div>
                  </div>
                )}

                {error && <div className="error">{error}</div>}
                {results.length > 0 && (
                  <div className="result-count">{results.length} ROMS FOUND</div>
                )}

                <div className="results">
                  {searched && !loading && (
                    <div className="search-notice">
                      ⚠ IF RESULTS ARE MISSING OR UNEXPECTED, ONE OR MORE REMOTE SITES MAY BE TEMPORARILY UNREACHABLE.
                    </div>
                  )}
                  {results.map((result, index) => {
                    const isOpen = expandedIndex === index;
                    const details = detailsCache[result.url];
                    const coverSrc = (details && details !== 'loading' && details !== 'error' && details.cover_image)
                      ? details.cover_image
                      : result.cover_image;
                    return (
                      <div key={index} className={`result-item${isOpen ? ' result-item--open' : ''}`}>
                        {coverSrc && (
                          <img
                            src={coverSrc}
                            alt={result.game_name}
                            className="cover-image"
                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                          />
                        )}
                        <div className="result-info">
                          <h3>{result.game_name}</h3>
                          <p><strong>System:</strong> {SYSTEM_LABELS[result.system] || result.system.toUpperCase()}</p>
                          <p><strong>Site:</strong> {result.site}</p>
                          {result.size && <p><strong>Size:</strong> {result.size}</p>}

                          {isOpen && (
                            <div className="detail-panel">
                              {details === 'loading' && (
                                <p className="detail-loading">LOADING<span className="loading-dots" /></p>
                              )}
                              {details === 'error' && (
                                <p className="detail-error">!! COULD NOT LOAD DETAILS</p>
                              )}
                              {details && details !== 'loading' && details !== 'error' && (
                                <div className="detail-fields">
                                  {details.year     && <p><strong>Year:</strong> {details.year}</p>}
                                  {details.region   && <p><strong>Region:</strong> {details.region}</p>}
                                  {details.genre    && <p><strong>Genre:</strong> {details.genre}</p>}
                                  {details.languages && <p><strong>Languages:</strong> {details.languages}</p>}
                                  {details.players  && <p><strong>Players:</strong> {details.players}</p>}
                                  {details.size     && <p><strong>Size:</strong> {details.size}</p>}
                                  {details.serial   && <p><strong>Serial:</strong> {details.serial}</p>}
                                  {details.rating   && <p><strong>Rating:</strong> {details.rating}</p>}
                                  {details.description && <p className="detail-desc">{details.description}</p>}
                                </div>
                              )}
                            </div>
                          )}

                          <div className="result-actions">
                            <button onClick={() => toggleDetails(index, result.url)}>
                              {isOpen ? '▼ HIDE' : '▶ DETAILS'}
                            </button>
                            <a href={result.url} target="_blank" rel="noopener noreferrer">
                              ↗ VISIT SITE
                            </a>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            } />

            <Route path="/legal" element={<Legal />} />
            <Route path="*" element={<Home />} />
          </Routes>
        </main>

        <footer className="footer">
          <div className="footer-inner">
            <p>© ROMULUS.ROCKS  ·  ROMS ARE FOR EVERYONE</p>
            <div className="footer-actions">
              <a className="footer-help" href="mailto:help@romulus.rocks">✉ HELP</a>
              <button className="footer-reset" onClick={resetSearch}>⟳ NEW SEARCH</button>
              <span className="visit-counter">
                ◉ {visits === null ? '······' : visits.toLocaleString()} VISITORS
              </span>
            </div>
          </div>
        </footer>
      </div>
  );
};

const ROMulus: React.FC = () => (
  <Router>
    <AppContent />
  </Router>
);

export default ROMulus;
